import { Link } from 'react-router-dom';
import { CTA, MicrosoftStoreButton, PageHero, ProductShot } from '../../components/Common';
import { DemoVideo } from '../../components/DemoVideo';
import { Seo } from '../../components/Seo';
import { track } from '../../components/analytics';
import { productOffers } from '../../data/offers';
import {
  categories,
  clientComparison,
  hindiDemoVideo,
  microsoftStore,
  site,
  webApp,
} from '../../data/site';
import { Head, ScreenshotGallery } from './sections';

const desktopWorkspaces = [
  [
    'Operations',
    'Trips, transactions, job cards, fuel and toll transactions, reconciliation, GPS and POD tracking.',
  ],
  [
    'Fleet',
    'Vehicles, parties, employees, firms, accounts, and inventory including serialized tyres.',
  ],
  [
    'Finance',
    'Accounts and reconciliation, party settlements, a Billing section with invoices, receipts, vouchers, GST and the TDS register, outstanding balances, and loan instalment schedules.',
  ],
  [
    'Insights',
    'Vehicle and trip-expense reports, trip margins, round-trip analysis, collections and risk, and the Data Quality Center.',
  ],
  [
    'Administration',
    'Users, roles, module permissions, billing profiles, connected devices, application settings, and data conflicts.',
  ],
  ['Tally', 'Export to Tally, trip register, contra report, balance sheet, and profit and loss.'],
];

export function Desktop() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `${site.name} for Windows`,
    applicationCategory: categories.schemaOrg,
    operatingSystem: 'Windows',
    publisher: { '@type': 'Organization', name: site.publisher },
    downloadUrl: microsoftStore.url,
    offers: productOffers(),
  };
  return (
    <>
      <Seo
        title="Transport software for Windows and macOS"
        breadcrumb="Desktop application"
        description="The complete offline-first workspace for logistics operations, workshop, finance, settlements, reporting, and administration. On the Microsoft Store for Windows."
        schema={schema}
      />
      <PageHero
        eyebrow="Desktop application"
        title="Your complete logistics operations workspace."
        description="Transport Book for Windows and macOS holds the detailed records, financial workflows, reporting, and administration behind day-to-day transport operations — and keeps working when the network does not."
      >
        <div className="actions">
          <MicrosoftStoreButton variant="badge" />
          <Link className="button secondary" to="/downloads/desktop">
            Other download options
          </Link>
        </div>
      </PageHero>
      <section className="section compact">
        <div className="container">
          <ProductShot
            slug="dashboard"
            alt="The Transport Book desktop dashboard showing working capital, receivables, payables, pending PODs, active reminders, and business performance."
            priority
            className="motion-item"
          />
        </div>
      </section>
      <DemoVideo video={hindiDemoVideo} placement="desktop" poster="trips" />
      <section className="section">
        <div className="container">
          <Head
            eyebrow="Full operational depth"
            title="Six workspaces, one set of records."
            body="Desktop is the primary surface for records, money, detailed reporting, and access management."
          />
          <div className="grid three">
            {desktopWorkspaces.map(([title, body]) => (
              <article className="card" key={title}>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section surface">
        <div className="container grid two">
          <div>
            <div className="eyebrow">Installing on Windows</div>
            <h2>The Store handles it, or you do.</h2>
            <p className="lead">
              The Microsoft Store listing is the simplest route: it installs the signed package and
              keeps it updated under the settings you control in Windows.
            </p>
            <p>
              For machines that cannot reach the Store, the site publishes a self-updating
              installer, plus an ARM64 package for macOS.
            </p>
            <div className="actions">
              <MicrosoftStoreButton />
              <Link className="button secondary" to="/downloads/desktop">
                Direct downloads
              </Link>
            </div>
          </div>
          <div className="grid">
            <article className="card">
              <h3>Microsoft Store</h3>
              <p>Signed, installed, and updated by Windows. Recommended for most machines.</p>
            </article>
            <article className="card">
              <h3>Installer (.exe)</h3>
              <p>
                Self-updating from the official release feed. For machines without Store access.
              </p>
            </article>
            <article className="card">
              <h3>Nothing to install?</h3>
              <p>
                The same workspace runs in the browser at{' '}
                <a
                  href={webApp.url}
                  rel="noopener"
                  onClick={() => track('open_web_app', { placement: 'desktop_page' })}
                >
                  my.transportbook.app
                </a>
                . <Link to="/web">How it differs from the desktop.</Link>
              </p>
            </article>
          </div>
        </div>
      </section>
      <ScreenshotGallery link={false} />
      <CTA />
    </>
  );
}

const mobileCapabilities = [
  ['Dashboard', 'Business metrics and the day’s operating position at a glance.'],
  ['Job cards', 'Raise, edit, and photograph workshop job cards from the yard or the roadside.'],
  ['Active trips', 'What is moving right now, with status and vehicle context.'],
  ['Outstanding and pending POD', 'The two follow-up reports that drive most field calls.'],
  ['GPS tracking', 'The live fleet map, with the trip behind each vehicle.'],
  ['Fuel limits', 'Card limits and live controls without opening a laptop.'],
  [
    'Master records',
    'Create and edit trips, trucks, employees, and parties on a workspace hosted on the Transport Book server; review them otherwise.',
  ],
];

export function Mobile() {
  return (
    <>
      <Seo
        title="Transport management app for Android"
        breadcrumb="Mobile companion"
        description="Android access to active trips, job cards, GPS tracking, outstanding and pending-POD reports, and fuel-card controls."
      />
      <PageHero
        eyebrow="Mobile companion"
        title="Keep the operation in reach when you leave the desk."
        description="Focused Android access to live operational records, job cards, reports, GPS context, and fuel controls — synced with the same records the desktop workspace uses."
      >
        <div className="actions">
          <Link className="button primary" to="/downloads/android">
            Download for Android
          </Link>
          <Link className="button secondary" to="/request-demo">
            Request a demo
          </Link>
        </div>
      </PageHero>
      <section className="section">
        <div className="container">
          <Head
            eyebrow="Focused by design"
            title="Field visibility, not a second copy of the desktop."
            body="Job cards, trips, and master records can be raised and corrected where the vehicle is. Detailed finance, administration, settlements, and Tally stay on the desktop and the web app."
          />
          <div className="grid three">
            {mobileCapabilities.map(([title, body]) => (
              <article className="card" key={title}>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <CTA />
    </>
  );
}

export function WebApp() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: webApp.name,
    url: webApp.url,
    applicationCategory: categories.schemaOrg,
    operatingSystem: 'Any',
    browserRequirements: webApp.requirements,
    description: webApp.tagline,
    publisher: { '@type': 'Organization', name: site.publisher },
    offers: productOffers(),
  };
  return (
    <>
      <Seo
        title="Online transport software in your browser"
        breadcrumb="Web app"
        description={webApp.tagline}
        schema={schema}
      />
      <PageHero
        eyebrow="Web app"
        title="Transport Book, with nothing to install."
        description={webApp.description}
      >
        <div className="actions">
          <a
            className="button primary"
            href={webApp.url}
            rel="noopener"
            onClick={() => track('open_web_app', { placement: 'web_page_cta' })}
          >
            Open the web app <span aria-hidden="true">↗</span>
          </a>
          <Link
            className="button secondary"
            to="/start-trial"
            onClick={() => track('trial_cta_click', { placement: 'web_page_hero' })}
          >
            Start free trial
          </Link>
        </div>
      </PageHero>
      <section className="section">
        <div className="container">
          <Head
            eyebrow="One workspace, two ways in"
            title="Every desktop screen, in a browser tab."
            body="There is no cut-down version. The web app is built from the desktop’s own screens and talks to the same server the desktops sync through."
          />
          <div className="grid three">
            {webApp.highlights.map(item => (
              <article className="card" key={item}>
                <p>{item}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section surface">
        <div className="container">
          <Head
            eyebrow="Desktop or browser?"
            title="Same work. Different place for the records."
            body="The desktop keeps a full copy of your data on the machine; the browser works on the organisation’s copy on the server. Everything else follows from that."
          />
          <div className="table-scroll">
            <table className="compare">
              <thead>
                <tr>
                  <th>&nbsp;</th>
                  <th>Desktop app</th>
                  <th>Web app</th>
                </tr>
              </thead>
              <tbody>
                {clientComparison.map(([aspect, desktop, web]) => (
                  <tr key={aspect}>
                    <td>{aspect}</td>
                    <td>{desktop}</td>
                    <td>{web}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="actions">
            <Link className="button secondary" to="/desktop">
              Explore desktop
            </Link>
            <Link className="button secondary" to="/downloads/desktop">
              Desktop downloads
            </Link>
          </div>
        </div>
      </section>
      <section className="section">
        <div className="container grid two">
          <article className="card">
            <h2 className="card-title">Who can use it</h2>
            <p>{webApp.availability}</p>
            <p>
              Sign in with the same organisation, user name, and password you use on the desktop.
              Your role applies exactly as it does there.
            </p>
          </article>
          <article className="card">
            <h2 className="card-title">What you need</h2>
            <p>{webApp.requirements}</p>
            <p>
              If the connection drops, the app says so at the foot of the screen, counts down to its
              next attempt, and picks up where you were once it is back. Nothing is stored in the
              browser between sessions.
            </p>
          </article>
        </div>
      </section>
      <CTA
        title="See it against your own operation"
        body="Ask for a demo and we will open the workspace in the browser with your kind of fleet, then show where the desktop still earns its place."
      />
    </>
  );
}
